import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { getBrandList, BrandNode, createBrand, BrandCreateRequest } from '../../utils/brand';
import { 
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Edit,
  Plus,
  Trash2,
  Image as ImageIcon,
  ChevronsDown,
  ChevronsUp,
  X
} from 'lucide-react';

// 브랜드 트리 노드 인터페이스
interface BrandConfigNode {
  id: string;
  name: string;
  logo?: string;
  code?: string;
  status?: string;
  children?: BrandConfigNode[];
}

// API BrandNode를 BrandConfigNode로 변환 (재귀적으로 subBrands 처리)
const convertBrandNodeToConfigNode = (node: BrandNode): BrandConfigNode => {
  return {
    id: node.code,
    name: node.name,
    logo: node.logo,
    code: node.code,
    status: node.status,
    children: node.subBrands && node.subBrands.length > 0 
      ? node.subBrands.map(convertBrandNodeToConfigNode)
      : undefined
  };
};

const BrandConfig: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl } = useParams<{ projectCode: string }>();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('brand-config');

  // Tree state
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [treeData, setTreeData] = useState<BrandConfigNode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [parentNode, setParentNode] = useState<BrandConfigNode | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    status: 'ACTIVE',
    sortOrder: 0
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Check if screen is mobile size
  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // URL의 projectCode 검증 및 현재 프로젝트와 일치 여부
  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  // 브랜드 트리 데이터 로드 및 모든 노드 펼치기
  useEffect(() => {
    if (!projectCodeFromUrl || !projectDetail) return;

    const fetchBrandList = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await getBrandList(projectCodeFromUrl);
        
        // API 응답이 이미 트리 구조이므로 subBrands를 children으로 변환
        const treeNodes: BrandConfigNode[] = response.brands.map(convertBrandNodeToConfigNode);
        
        setTreeData(treeNodes);
        
        // 모든 노드 ID를 수집하여 펼쳐진 상태로 설정
        const collectAllNodeIds = (nodes: BrandConfigNode[]): string[] => {
          const ids: string[] = [];
          nodes.forEach(node => {
            ids.push(node.id);
            if (node.children && node.children.length > 0) {
              ids.push(...collectAllNodeIds(node.children));
            }
          });
          return ids;
        };
        
        const allNodeIds = collectAllNodeIds(treeNodes);
        setExpandedNodes(new Set(allNodeIds));
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
        setError(err instanceof Error ? err.message : '브랜드 목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrandList();
  }, [projectCodeFromUrl, projectDetail]);

  // 트리 노드 토글
  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // 브랜드 수정 핸들러
  const handleEdit = (node: BrandConfigNode, e: React.MouseEvent) => {
    e.stopPropagation();
    if (node.code) {
      navigate(`/projects/${projectCodeFromUrl}/brands/${encodeURIComponent(node.code)}/update`);
    }
  };

  // 하위 브랜드 추가 핸들러
  const handleAddChild = (node: BrandConfigNode, e: React.MouseEvent) => {
    e.stopPropagation();
    setParentNode(node);
    // 같은 부모를 가진 형제 노드 수 + 1을 기본 sortOrder로 설정 (순서는 1부터 시작)
    const siblingCount = node.children?.length || 0;
    setFormData({
      code: '',
      name: '',
      status: 'ACTIVE',
      sortOrder: siblingCount + 1
    });
    setFormErrors({});
    setIsAddModalOpen(true);
  };

  // 브랜드 삭제 핸들러
  const handleDelete = (node: BrandConfigNode, e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: 삭제 확인 모달 표시
    if (window.confirm(`"${node.name}" 브랜드를 삭제하시겠습니까?`)) {
      console.log('브랜드 삭제:', node);
      // TODO: 실제 삭제 API 호출
    }
  };

  // 최상위 브랜드 추가 핸들러
  const handleAddBrand = () => {
    setParentNode(null);
    // 최상위 브랜드 수 + 1을 기본 sortOrder로 설정 (순서는 1부터 시작)
    setFormData({
      code: '',
      name: '',
      status: 'ACTIVE',
      sortOrder: treeData.length + 1
    });
    setFormErrors({});
    setIsAddModalOpen(true);
  };

  // 브랜드 추가 폼 제출
  const handleSubmitBrand = async () => {
    // 유효성 검사
    const errors: Record<string, string> = {};
    if (!formData.code.trim()) {
      errors.code = '브랜드 코드를 입력해주세요.';
    }
    if (!formData.name.trim()) {
      errors.name = '브랜드명을 입력해주세요.';
    }
    if (formData.code.length > 20) {
      errors.code = '브랜드 코드는 20자 이하여야 합니다.';
    }
    if (formData.name.length > 30) {
      errors.name = '브랜드명은 30자 이하여야 합니다.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (!projectCodeFromUrl || !projectDetail) {
      setFormErrors({ submit: '프로젝트 정보가 없습니다.' });
      return;
    }

    try {
      setIsSubmitting(true);
      setFormErrors({});

      // 부모 노드 정보 가져오기 (하위 브랜드인 경우)
      let parentCode: string | undefined;
      let depth = 0;
      let sortOrder = formData.sortOrder || 0;

      if (parentNode && parentNode.code) {
        parentCode = parentNode.code;
        // 부모의 depth + 1
        const findNodeDepth = (nodes: BrandConfigNode[], targetCode: string, currentDepth: number = 0): number => {
          for (const node of nodes) {
            if (node.code === targetCode) {
              return currentDepth;
            }
            if (node.children) {
              const found = findNodeDepth(node.children, targetCode, currentDepth + 1);
              if (found !== -1) return found;
            }
          }
          return -1;
        };
        const parentDepth = findNodeDepth(treeData, parentCode);
        depth = parentDepth !== -1 ? parentDepth + 1 : 0;
        // sortOrder가 설정되지 않았으면 같은 부모를 가진 형제 노드 수 + 1을 사용 (순서는 1부터 시작)
        if (sortOrder === 0 && parentNode.children) {
          sortOrder = parentNode.children.length + 1;
        } else if (sortOrder === 0) {
          sortOrder = 1; // 형제 노드가 없으면 1
        }
      } else {
        // 최상위 브랜드
        depth = 0;
        // sortOrder가 설정되지 않았으면 최상위 브랜드 수 + 1을 사용 (순서는 1부터 시작)
        if (sortOrder === 0) {
          sortOrder = treeData.length + 1;
        }
      }

      const brandData: BrandCreateRequest = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        status: formData.status,
        parentCode: parentNode?.code || undefined, // 선택된 브랜드의 코드를 parentCode로 설정
        depth,
        sortOrder
      };

      console.log('브랜드 생성 요청:', brandData);
      await createBrand(projectCodeFromUrl, brandData);

      // 성공 시 모달 닫고 목록 새로고침
      setIsAddModalOpen(false);
      setFormData({
        code: '',
        name: '',
        status: 'ACTIVE',
        sortOrder: 0
      });
      setFormErrors({});

      // 브랜드 목록 다시 불러오기
      const response = await getBrandList(projectCodeFromUrl);
      const treeNodes: BrandConfigNode[] = response.brands.map(convertBrandNodeToConfigNode);
      setTreeData(treeNodes);
      
      // 모든 노드 펼치기
      const collectAllNodeIds = (nodes: BrandConfigNode[]): string[] => {
        const ids: string[] = [];
        nodes.forEach(node => {
          ids.push(node.id);
          if (node.children && node.children.length > 0) {
            ids.push(...collectAllNodeIds(node.children));
          }
        });
        return ids;
      };
      const allNodeIds = collectAllNodeIds(treeNodes);
      setExpandedNodes(new Set(allNodeIds));
    } catch (err) {
      console.error('브랜드 생성 오류:', err);
      setFormErrors({ 
        submit: err instanceof Error ? err.message : '브랜드 생성 중 오류가 발생했습니다.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 브랜드 추가 모달 닫기
  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setParentNode(null);
    setFormData({
      code: '',
      name: '',
      status: 'ACTIVE',
      sortOrder: 0
    });
    setFormErrors({});
  };

  // 전체 펼치기
  const handleExpandAll = () => {
    const collectAllNodeIds = (nodes: BrandConfigNode[]): string[] => {
      const ids: string[] = [];
      nodes.forEach(node => {
        ids.push(node.id);
        if (node.children && node.children.length > 0) {
          ids.push(...collectAllNodeIds(node.children));
        }
      });
      return ids;
    };
    const allNodeIds = collectAllNodeIds(treeData);
    setExpandedNodes(new Set(allNodeIds));
  };

  // 전체 접기
  const handleCollapseAll = () => {
    setExpandedNodes(new Set());
  };

  // 브랜드 통계 계산
  const calculateBrandStats = () => {
    const countAllNodes = (nodes: BrandConfigNode[]): number => {
      let count = 0;
      nodes.forEach(node => {
        count++;
        if (node.children && node.children.length > 0) {
          count += countAllNodes(node.children);
        }
      });
      return count;
    };

    const countActiveNodes = (nodes: BrandConfigNode[]): number => {
      let count = 0;
      nodes.forEach(node => {
        if (node.status === 'ACTIVE') {
          count++;
        }
        if (node.children && node.children.length > 0) {
          count += countActiveNodes(node.children);
        }
      });
      return count;
    };

    const totalBrands = countAllNodes(treeData);
    const topLevelBrands = treeData.length;
    const activeBrands = countActiveNodes(treeData);

    return {
      totalBrands,
      activeBrands,
      topLevelBrands
    };
  };

  const brandStats = calculateBrandStats();

  // 트리 노드 렌더링
  const renderTreeNode = (node: BrandConfigNode, level: number = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node.id} className="mb-1">
        <div
          className="flex items-center py-3 px-4 rounded-lg transition-colors cursor-pointer text-gray-600 hover:bg-gray-100"
          onClick={() => toggleNode(node.id)}
          style={{ paddingLeft: `${level * 24 + 16}px` }}
        >
          {/* 펼침/접힘 아이콘 */}
          <div className="flex-shrink-0 w-8 flex items-center justify-center">
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )
            ) : (
              <div className="w-4" />
            )}
          </div>

          {/* 로고 */}
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden mr-3">
            {node.logo ? (
              <img 
                src={node.logo} 
                alt={node.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <ImageIcon className={`w-5 h-5 text-gray-400 ${node.logo ? 'hidden' : ''}`} />
          </div>

          {/* 브랜드명 */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{node.name}</span>
              {node.code && (
                <span className="text-sm text-gray-400">{node.code}</span>
              )}
            </div>
          </div>

          {/* 액션 아이콘 */}
          <div className="flex-shrink-0 flex items-center space-x-2 ml-3" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => handleEdit(node, e)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
              title="수정"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => handleAddChild(node, e)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
              title="하위 브랜드 추가"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => handleDelete(node, e)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-red-600 hover:text-red-700 transition-colors"
              title="삭제"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* 하위 노드들 */}
        {hasChildren && isExpanded && (
          <div className="mt-1 space-y-1">
            {node.children!.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // 프로젝트 정보가 없을 때 처리
  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Project Sidebar */}
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <Topbar
          title="브랜드 설정"
          subtitle={projectCodeFromUrl && projectDetail ? `${projectDetail.name} (${projectCodeFromUrl})` : '브랜드 계층 구조를 관리하고 설정합니다'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        {/* Brand Config Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          {/* Filter/Action Area */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              {/* 액션 버튼 */}
              <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                <button
                  onClick={handleExpandAll}
                  className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 hover:text-gray-900 transition-colors"
                  title="전체 펼치기"
                >
                  <ChevronsDown className="w-4 h-4 mr-1" />
                  전체 펼치기
                </button>
                <button
                  onClick={handleCollapseAll}
                  className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 hover:text-gray-900 transition-colors"
                  title="전체 접기"
                >
                  <ChevronsUp className="w-4 h-4 mr-1" />
                  전체 접기
                </button>
                <button
                  onClick={handleAddBrand}
                  className="inline-flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  브랜드 추가
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-sm text-gray-600">
                총 <span className="font-medium text-gray-900">{brandStats.totalBrands}</span>개 브랜드
              </p>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                  활성 브랜드: <span className="font-medium text-gray-900 ml-1">{brandStats.activeBrands}</span>
                </span>
                <span className="flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-1"></span>
                  최상위 브랜드: <span className="font-medium text-gray-900 ml-1">{brandStats.topLevelBrands}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Brand Tree List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6">
            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="text-red-500 mb-4">
                  <Trash2 className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-red-600 mb-4 text-center max-w-md mx-auto">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    if (projectCodeFromUrl) {
                        getBrandList(projectCodeFromUrl)
                          .then(response => {
                            const treeNodes: BrandConfigNode[] = response.brands.map(convertBrandNodeToConfigNode);
                            setTreeData(treeNodes);
                            const collectAllNodeIds = (nodes: BrandConfigNode[]): string[] => {
                              const ids: string[] = [];
                              nodes.forEach(node => {
                                ids.push(node.id);
                                if (node.children && node.children.length > 0) {
                                  ids.push(...collectAllNodeIds(node.children));
                                }
                              });
                              return ids;
                            };
                            const allNodeIds = collectAllNodeIds(treeNodes);
                            setExpandedNodes(new Set(allNodeIds));
                          })
                          .catch(err => {
                            console.error('브랜드 목록 재조회 오류:', err);
                            setError(err instanceof Error ? err.message : '브랜드 목록을 불러오는 중 오류가 발생했습니다.');
                          });
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && treeData.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">등록된 브랜드가 없습니다.</p>
                <button
                  onClick={handleAddBrand}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  브랜드 추가
                </button>
              </div>
            )}

            {/* Tree Data */}
            {!isLoading && !error && treeData.length > 0 && (
              <div className="space-y-1">
                {treeData.map(node => renderTreeNode(node))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* 브랜드 추가 모달 */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* 모달 헤더 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {parentNode ? '하위 브랜드 추가' : '브랜드 추가'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* 모달 본문 */}
            <div className="p-6 space-y-4">
              {parentNode && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">부모 브랜드:</span> {parentNode.name}
                  </p>
                </div>
              )}

              {/* 브랜드 코드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  브랜드 코드 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => {
                    const next = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
                    setFormData({ ...formData, code: next });
                    setFormErrors((prev) => {
                      if (!prev.code) return prev;
                      const next = { ...prev };
                      delete next.code;
                      return next;
                    });
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.code ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="영문, 숫자, _, - (20자 이하)"
                  maxLength={20}
                />
                {formErrors.code && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.code}</p>
                )}
              </div>

              {/* 브랜드명 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  브랜드명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="브랜드명을 입력하세요"
                  maxLength={30}
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                )}
              </div>

              {/* 상태 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  상태 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ACTIVE">활성</option>
                  <option value="INACTIVE">비활성</option>
                </select>
              </div>

              {/* 순서 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  순서
                </label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 1 })}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.sortOrder ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="순서를 입력하세요"
                  min="1"
                />
                {formErrors.sortOrder && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.sortOrder}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  브랜드 표시 순서를 설정합니다 (1 이상의 숫자)
                </p>
              </div>

              {/* 에러 메시지 */}
              {formErrors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{formErrors.submit}</p>
                </div>
              )}
            </div>

            {/* 모달 푸터 */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors"
                disabled={isSubmitting}
              >
                취소
              </button>
              <button
                onClick={handleSubmitBrand}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '추가 중...' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandConfig;